import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Info,
  Wrench,
  FileText,
  Settings,
  CheckCircle
} from 'lucide-react';

const CUSTOM_MAKE_OPTION = 'Other (Custom)';

const VEHICLE_MAKES = [
  'Freightliner',
  'Volvo',
  'Peterbilt',
  'Kenworth',
  'International',
  'Mack',
  'Western Star',
  'Sterling',
  'Ford (Commercial)',
  'Hino',
  CUSTOM_MAKE_OPTION
];

const VEHICLE_MODELS_BY_MAKE = {
  Freightliner: ['Cascadia', 'Coronado', 'Columbia', 'M2 112', '122SD'],
  Volvo: ['VNL 760', 'VNL 860', 'VNR 640', 'VNX 300', 'VHD 400'],
  Peterbilt: ['579', '389', '567', '589', '536'],
  Kenworth: ['T680', 'T880', 'W990', 'W900', 'T480'],
  International: ['LT625', 'LT625 Sleeper', 'HX520', 'MV607', 'RH Series'],
  Mack: ['Anthem', 'Pinnacle', 'Granite', 'LR Electric', 'MD Series'],
  'Western Star': ['5700XE', '49X', '47X', '57X', '6900XD'],
  Sterling: ['A-Line', 'Acterra', 'L-Line'],
  'Ford (Commercial)': ['F-750', 'F-650', 'L9000', 'CLT9000'],
  Hino: ['XL8', 'XL7', '268A', '338']
};

const US_STATE_OPTIONS = [
  { value: 'AL', label: 'Alabama (AL)' },
  { value: 'AK', label: 'Alaska (AK)' },
  { value: 'AZ', label: 'Arizona (AZ)' },
  { value: 'AR', label: 'Arkansas (AR)' },
  { value: 'CA', label: 'California (CA)' },
  { value: 'CO', label: 'Colorado (CO)' },
  { value: 'CT', label: 'Connecticut (CT)' },
  { value: 'DE', label: 'Delaware (DE)' },
  { value: 'FL', label: 'Florida (FL)' },
  { value: 'GA', label: 'Georgia (GA)' },
  { value: 'HI', label: 'Hawaii (HI)' },
  { value: 'ID', label: 'Idaho (ID)' },
  { value: 'IL', label: 'Illinois (IL)' },
  { value: 'IN', label: 'Indiana (IN)' },
  { value: 'IA', label: 'Iowa (IA)' },
  { value: 'KS', label: 'Kansas (KS)' },
  { value: 'KY', label: 'Kentucky (KY)' },
  { value: 'LA', label: 'Louisiana (LA)' },
  { value: 'ME', label: 'Maine (ME)' },
  { value: 'MD', label: 'Maryland (MD)' },
  { value: 'MA', label: 'Massachusetts (MA)' },
  { value: 'MI', label: 'Michigan (MI)' },
  { value: 'MN', label: 'Minnesota (MN)' },
  { value: 'MS', label: 'Mississippi (MS)' },
  { value: 'MO', label: 'Missouri (MO)' },
  { value: 'MT', label: 'Montana (MT)' },
  { value: 'NE', label: 'Nebraska (NE)' },
  { value: 'NV', label: 'Nevada (NV)' },
  { value: 'NH', label: 'New Hampshire (NH)' },
  { value: 'NJ', label: 'New Jersey (NJ)' },
  { value: 'NM', label: 'New Mexico (NM)' },
  { value: 'NY', label: 'New York (NY)' },
  { value: 'NC', label: 'North Carolina (NC)' },
  { value: 'ND', label: 'North Dakota (ND)' },
  { value: 'OH', label: 'Ohio (OH)' },
  { value: 'OK', label: 'Oklahoma (OK)' },
  { value: 'OR', label: 'Oregon (OR)' },
  { value: 'PA', label: 'Pennsylvania (PA)' },
  { value: 'RI', label: 'Rhode Island (RI)' },
  { value: 'SC', label: 'South Carolina (SC)' },
  { value: 'SD', label: 'South Dakota (SD)' },
  { value: 'TN', label: 'Tennessee (TN)' },
  { value: 'TX', label: 'Texas (TX)' },
  { value: 'UT', label: 'Utah (UT)' },
  { value: 'VT', label: 'Vermont (VT)' },
  { value: 'VA', label: 'Virginia (VA)' },
  { value: 'WA', label: 'Washington (WA)' },
  { value: 'WV', label: 'West Virginia (WV)' },
  { value: 'WI', label: 'Wisconsin (WI)' },
  { value: 'WY', label: 'Wyoming (WY)' }
];

const ENGINE_OPTIONS = [
  'Cummins X15 Efficiency Series',
  'Cummins X15 Performance Series',
  'PACCAR MX-13',
  'PACCAR MX-11',
  'Detroit DD15',
  'Detroit DD13',
  'Navistar A26',
  'Mack MP8',
  'Volvo D13',
  'Volvo D11',
  'Cat C15 (Legacy)',
  'Cummins L9'
];

// The backend stores odometer, GVWR, empty weight and axles as whole numbers.
const isWholeNumber = (value) => value === '' || value === null || value === undefined || Number.isInteger(Number(value));

const isNotFutureDate = (value) => {
  if (!value) {
    return true;
  }
  const [year, month, day] = value.split('-').map(Number);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(year, month - 1, day) <= today;
};

const toUpperTrimmed = (value) => (typeof value === 'string' ? value.trim().toUpperCase() : value);

const createDefaultFormValues = () => ({
  // Basic Information
  vehicleNumber: '',
  make: '',
  customMake: '',
  model: '',
  year: new Date().getFullYear(),
  vin: '',
  type: 'truck',
  status: 'active',
  purchaseDate: '',
  purchasePrice: '',

  // Registration Details
  licensePlate: '',
  plateState: '',
  registrationState: '',

  // DOT Numbers
  dotNumber: '',
  mcNumber: '',
  scacCode: '',
  iccNumber: '',
  hazmatPermit: false,
  hazmatPermitExpiry: '',

  // Specifications
  engine: '',
  transmission: '',
  fuelType: 'diesel',
  gvwr: '',
  emptyWeight: '',
  capacity: '',
  wheelbase: '',
  sleeper: false,
  axles: 2,
  color: '',
  odometer: ''
});

const VehicleForm = ({ vehicle = null, initialValues = null, onSubmit, onCancel, onSaveDraft, onStartUploadingDocuments }) => {
  const [activeSection, setActiveSection] = useState('basic');
  const [vehicleCreated, setVehicleCreated] = useState(false);
  const [createdVehicleData, setCreatedVehicleData] = useState(null);
  
  const defaultFormValues = useMemo(
    () => initialValues || vehicle || createDefaultFormValues(),
    [initialValues, vehicle]
  );

  const {
    register,
    handleSubmit,
    trigger,
    getValues,
    reset,
    setValue,
    formState: { errors, isSubmitting },
    watch
  } = useForm({
    defaultValues: defaultFormValues
  });

  useEffect(() => {
    if (vehicle) {
      reset(vehicle);
    } else if (initialValues) {
      reset(initialValues);
    } else {
      reset(createDefaultFormValues());
    }
  }, [initialValues, vehicle, reset]);

  const makeValue = watch('make');
  const engineValue = watch('engine');
  const isCustomMake = makeValue === CUSTOM_MAKE_OPTION;

  const makeOptions = useMemo(() => {
    const options = [...VEHICLE_MAKES];
    if (makeValue && !options.includes(makeValue)) {
      options.unshift(makeValue);
    }
    return Array.from(new Set(options));
  }, [makeValue]);

  const modelSuggestions = makeValue ? VEHICLE_MODELS_BY_MAKE[makeValue] || [] : [];

  const stateOptions = useMemo(() => {
    return US_STATE_OPTIONS;
  }, []);

  const engineOptions = useMemo(() => {
    const options = [...ENGINE_OPTIONS];
    if (defaultFormValues.engine && !options.includes(defaultFormValues.engine)) {
      options.unshift(defaultFormValues.engine);
    }
    if (engineValue && !options.includes(engineValue)) {
      options.unshift(engineValue);
    }
    return Array.from(new Set(options));
  }, [defaultFormValues.engine, engineValue]);

  const sections = [
    { id: 'basic', name: 'Vehicle Details', icon: Info },
    { id: 'specifications', name: 'Specifications', icon: Settings }
  ];

  const sectionOrder = sections.map(section => section.id);

  const sectionValidationMap = {
    basic: [
      'vehicleNumber', 'make', 'customMake', 'model', 'year', 'type', 'status', 'purchaseDate', 'vin', 'purchasePrice',
      'odometer', 'fuelType', 'licensePlate', 'plateState', 'registrationState'
    ],
    specifications: ['axles', 'gvwr', 'emptyWeight', 'capacity', 'wheelbase', 'hazmatPermitExpiry']
  };

  const fieldSectionMap = useMemo(() => {
    const map = {};
    Object.entries(sectionValidationMap).forEach(([sectionId, fields]) => {
      fields.forEach((field) => {
        map[field] = sectionId;
      });
    });
    return map;
  }, []);

  const vehicleTypes = [
    'truck', 'tractor', 'trailer', 'straight-truck', 'van', 'pickup', 'other'
  ];

  const vehicleStatuses = [
    'active', 'maintenance', 'out-of-service', 'retired'
  ];

  const fuelTypes = [
    'diesel', 'gasoline', 'electric', 'hybrid', 'natural-gas', 'propane'
  ];

  const transmissionTypes = [
    'manual', 'automatic', 'automated-manual', 'cvt'
  ];

  const currentYear = new Date().getFullYear();
  const years = useMemo(() => {
    const options = Array.from({ length: 31 }, (_, i) => currentYear + 1 - i);
    const existingYear = Number(defaultFormValues.year);
    if (Number.isInteger(existingYear) && !options.includes(existingYear)) {
      options.push(existingYear);
    }
    return options;
  }, [currentYear, defaultFormValues.year]);

  const onFormSubmit = async (formValues) => {
    const { customMake, ...data } = formValues;
    if (data.make === CUSTOM_MAKE_OPTION) {
      data.make = (customMake || '').trim();
    }

    try {
      await onSubmit(data);
      // If creating a new vehicle (not editing), show success message
      if (!vehicle) {
        setCreatedVehicleData(data);
        setVehicleCreated(true);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const renderBasicInfo = () => (
    <div className="space-y-6">
      {/* Basic Information Section */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">Vehicle Name/Number *</label>
              <input
                type="text"
                {...register('vehicleNumber', {
                  required: 'Vehicle number is required',
                  maxLength: { value: 50, message: 'Vehicle number cannot exceed 50 characters' }
                })}
                placeholder="e.g., T-001, TRK-123"
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.vehicleNumber ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.vehicleNumber && (
                <p className="mt-1 text-sm text-red-600">{errors.vehicleNumber.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Make *</label>
              <select
                {...register('make', {
                  required: 'Make is required',
                  onChange: () => setValue('model', '')
                })}
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.make ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select make</option>
                {makeOptions.map(make => (
                  <option key={make} value={make}>{make}</option>
                ))}
              </select>
              {errors.make && (
                <p className="mt-1 text-sm text-red-600">{errors.make.message}</p>
              )}
            </div>

            {isCustomMake && (
              <div>
                <label className="block text-sm font-medium text-gray-500">Custom Make *</label>
                <input
                  type="text"
                  {...register('customMake', {
                    validate: (value) => !isCustomMake || Boolean(value?.trim()) || 'Please enter the make',
                    maxLength: { value: 100, message: 'Make cannot exceed 100 characters' }
                  })}
                  placeholder="e.g., Navistar"
                  className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.customMake ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.customMake && (
                  <p className="mt-1 text-sm text-red-600">{errors.customMake.message}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-500">Model *</label>
              <input
                type="text"
                list="vehicle-model-suggestions"
                {...register('model', {
                  required: 'Model is required',
                  maxLength: { value: 100, message: 'Model cannot exceed 100 characters' }
                })}
                disabled={!makeValue}
                placeholder={makeValue ? 'Select or type a model' : 'Select a make first'}
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.model ? 'border-red-500' : 'border-gray-300'
                } ${!makeValue ? 'bg-gray-100 text-gray-500' : ''}`}
              />
              <datalist id="vehicle-model-suggestions">
                {modelSuggestions.map(model => (
                  <option key={model} value={model} />
                ))}
              </datalist>
              {errors.model && (
                <p className="mt-1 text-sm text-red-600">{errors.model.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Year *</label>
              <select
                {...register('year', { required: 'Year is required' })}
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.year ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              {errors.year && (
                <p className="mt-1 text-sm text-red-600">{errors.year.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Vehicle Type *</label>
              <select
                {...register('type', { required: 'Vehicle type is required' })}
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.type ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {vehicleTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
                  </option>
                ))}
              </select>
              {errors.type && (
                <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Status *</label>
              <select
                {...register('status', { required: 'Status is required' })}
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.status ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {vehicleStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                  </option>
                ))}
              </select>
              {errors.status && (
                <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Purchase Date</label>
              <input
                type="date"
                {...register('purchaseDate', {
                  validate: (value) => isNotFutureDate(value) || 'Purchase date cannot be in the future'
                })}
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.purchaseDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.purchaseDate && (
                <p className="mt-1 text-sm text-red-600">{errors.purchaseDate.message}</p>
              )}
            </div>

            <div className="lg:col-span-3">
              <label className="block text-sm font-medium text-gray-500">VIN (Vehicle Identification Number) *</label>
              <input
                type="text"
                {...register('vin', {
                  required: 'VIN is required',
                  // The input only displays uppercase via CSS; store it uppercase so it matches the VIN rule.
                  setValueAs: toUpperTrimmed,
                  minLength: { value: 17, message: 'VIN must be exactly 17 characters' },
                  maxLength: { value: 17, message: 'VIN must be exactly 17 characters' },
                  pattern: {
                    value: /^[A-HJ-NPR-Z0-9]{17}$/,
                    message: 'Please enter a valid VIN (17 characters, no I, O, or Q)'
                  }
                })}
                placeholder="Enter 17-character VIN"
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono ${
                  errors.vin ? 'border-red-500' : 'border-gray-300'
                }`}
                style={{ textTransform: 'uppercase' }}
              />
              {errors.vin && (
                <p className="mt-1 text-sm text-red-600">{errors.vin.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Purchase Price</label>
              <input
                type="number"
                {...register('purchasePrice', {
                  min: { value: 0, message: 'Purchase price cannot be negative' }
                })}
                placeholder="Enter purchase price"
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.purchasePrice ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.purchasePrice && (
                <p className="mt-1 text-sm text-red-600">{errors.purchasePrice.message}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Current Status Section */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Current Status</h3>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">Current Odometer (miles)</label>
              <input
                type="number"
                {...register('odometer', {
                  min: { value: 0, message: 'Odometer cannot be negative' },
                  max: { value: 9999999, message: 'Odometer cannot exceed 9,999,999 miles' },
                  validate: (value) => isWholeNumber(value) || 'Odometer must be a whole number'
                })}
                placeholder="e.g., 125000"
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.odometer ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.odometer && (
                <p className="mt-1 text-sm text-red-600">{errors.odometer.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Fuel Type *</label>
              <select
                {...register('fuelType', { required: 'Fuel type is required' })}
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.fuelType ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {fuelTypes.map((fuel) => (
                  <option key={fuel} value={fuel}>
                    {fuel.charAt(0).toUpperCase() + fuel.slice(1).replace('-', ' ')}
                  </option>
                ))}
              </select>
              {errors.fuelType && (
                <p className="mt-1 text-sm text-red-600">{errors.fuelType.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Color</label>
              <input
                type="text"
                {...register('color')}
                placeholder="e.g., White, Blue"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Registration Details Section */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Registration Details</h3>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">License Plate *</label>
              <input
                type="text"
                {...register('licensePlate', {
                  required: 'License plate is required',
                  setValueAs: toUpperTrimmed,
                  pattern: {
                    value: /^[A-Z0-9\-\s]{1,12}$/,
                    message: 'License plate must be 1-12 letters, numbers, spaces, or dashes'
                  }
                })}
                placeholder="e.g., ABC-1234"
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.licensePlate ? 'border-red-500' : 'border-gray-300'
                }`}
                style={{ textTransform: 'uppercase' }}
              />
              {errors.licensePlate && (
                <p className="mt-1 text-sm text-red-600">{errors.licensePlate.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Plate State *</label>
              <select
                {...register('plateState', { required: 'Plate state is required' })}
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.plateState ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select plate state</option>
                {stateOptions.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              {errors.plateState && (
                <p className="mt-1 text-sm text-red-600">{errors.plateState.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Registration State *</label>
              <select
                {...register('registrationState', { required: 'Registration state is required' })}
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.registrationState ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select registration state</option>
                {stateOptions.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              {errors.registrationState && (
                <p className="mt-1 text-sm text-red-600">{errors.registrationState.message}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSpecifications = () => (
    <div className="space-y-6">
      {/* Engine & Drivetrain Section */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Engine & Drivetrain</h3>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">Engine</label>
              <select
                {...register('engine')}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select engine</option>
                {engineOptions.map(engine => (
                  <option key={engine} value={engine}>{engine}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Transmission</label>
              <select
                {...register('transmission')}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select transmission type</option>
                {transmissionTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Number of Axles</label>
              <input
                type="number"
                {...register('axles', {
                  min: { value: 2, message: 'Must have at least 2 axles' },
                  max: { value: 20, message: 'Cannot exceed 20 axles' },
                  validate: (value) => isWholeNumber(value) || 'Axles must be a whole number'
                })}
                placeholder="e.g., 2"
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.axles ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.axles && (
                <p className="mt-1 text-sm text-red-600">{errors.axles.message}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Weight & Capacity Section */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Weight & Capacity</h3>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">GVWR (lbs)</label>
              <input
                type="number"
                {...register('gvwr', {
                  min: { value: 0, message: 'GVWR cannot be negative' },
                  max: { value: 100000, message: 'GVWR cannot exceed 100,000 lbs' },
                  validate: (value) => isWholeNumber(value) || 'GVWR must be a whole number'
                })}
                placeholder="e.g., 80000"
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.gvwr ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.gvwr && (
                <p className="mt-1 text-sm text-red-600">{errors.gvwr.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Empty Weight (lbs)</label>
              <input
                type="number"
                {...register('emptyWeight', {
                  min: { value: 0, message: 'Empty weight cannot be negative' },
                  max: { value: 100000, message: 'Empty weight cannot exceed 100,000 lbs' },
                  validate: (value) => isWholeNumber(value) || 'Empty weight must be a whole number'
                })}
                placeholder="e.g., 15000"
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.emptyWeight ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.emptyWeight && (
                <p className="mt-1 text-sm text-red-600">{errors.emptyWeight.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Capacity (lbs)</label>
              <input
                type="number"
                {...register('capacity', {
                  min: { value: 0, message: 'Capacity cannot be negative' }
                })}
                placeholder="e.g., 65000"
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.capacity ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.capacity && (
                <p className="mt-1 text-sm text-red-600">{errors.capacity.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Wheelbase (inches)</label>
              <input
                type="number"
                {...register('wheelbase', {
                  min: { value: 0, message: 'Wheelbase cannot be negative' }
                })}
                placeholder="e.g., 244"
                className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.wheelbase ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.wheelbase && (
                <p className="mt-1 text-sm text-red-600">{errors.wheelbase.message}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Features</h3>
        </div>
        <div className="px-6 py-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="sleeper"
              {...register('sleeper')}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="sleeper" className="ml-2 block text-sm font-medium text-gray-700">
              Has Sleeper Berth
            </label>
          </div>

          <div className="mt-4">
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="hazmatPermit"
                {...register('hazmatPermit')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="hazmatPermit" className="ml-2 block text-sm font-medium text-gray-700">
                HAZMAT Permit
              </label>
            </div>

            {watch('hazmatPermit') && (
              <div>
                <label className="block text-sm font-medium text-gray-500">HAZMAT Permit Expiry *</label>
                <input
                  type="date"
                  {...register('hazmatPermitExpiry', { 
                    required: watch('hazmatPermit') ? 'HAZMAT permit expiry is required when permit is selected' : false,
                    validate: {
                      futureDate: (value) => {
                        if (!watch('hazmatPermit')) return true;
                        const expDate = new Date(value);
                        const today = new Date();
                        return expDate > today || 'HAZMAT permit expiry must be in the future';
                      }
                    }
                  })}
                  className={`mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.hazmatPermitExpiry ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.hazmatPermitExpiry && (
                  <p className="mt-1 text-sm text-red-600">{errors.hazmatPermitExpiry.message}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const SuccessModal = () => {
    if (!vehicleCreated) return null;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
          {/* Success Header */}
          <div className="bg-green-50 border-b border-green-200 px-6 py-4 rounded-t-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-12 w-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-2xl font-bold text-green-900">Vehicle Created Successfully!</h3>
                <p className="text-sm text-green-700 mt-1">
                  {createdVehicleData?.vehicleNumber} - {createdVehicleData?.year} {createdVehicleData?.make} {createdVehicleData?.model}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <FileText className="h-5 w-5 text-blue-600 mr-2" />
                Upload Vehicle Documents (Optional)
              </h4>
              <p className="text-gray-700 mb-4">
                Would you like to upload important vehicle documents now? This will help you:
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Get automatic reminders when documents expire</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Stay compliant with regulations</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Keep all vehicle documents organized in one place</span>
                </li>
              </ul>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Recommended documents:</strong> Vehicle Registration, Insurance Certificate, Title, DOT Inspection Report, and other permits.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex flex-col sm:flex-row sm:justify-end gap-3">
            <button
              onClick={onCancel}
              className="w-full sm:w-auto px-6 py-2.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Add Later
            </button>
            <button
              onClick={() => {
                if (onStartUploadingDocuments) {
                  onStartUploadingDocuments();
                } else {
                  onCancel();
                }
              }}
              className="w-full sm:w-auto px-6 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center justify-center"
            >
              <FileText className="h-4 w-4 mr-2" />
              Start Uploading
            </button>
          </div>
        </div>
      </div>
    );
  };

  const findFirstErrorField = (errorObject) => {
    for (const key of Object.keys(errorObject)) {
      const value = errorObject[key];
      if (!value) continue;

      if (value?.message || value?.type || value?.types) {
        return key;
      }

      if (typeof value === 'object') {
        const nested = findFirstErrorField(value);
        if (nested) {
          return `${key}.${nested}`;
        }
      }
    }
    return null;
  };

  const handleFormErrors = (formErrors) => {
    const firstErrorField = findFirstErrorField(formErrors);
    if (!firstErrorField) {
      return;
    }

    const [rootField] = firstErrorField.split('.');
    const targetSection = fieldSectionMap[firstErrorField] || fieldSectionMap[rootField];
    if (targetSection) {
      setActiveSection(targetSection);
    }
  };

  const submitForm = handleSubmit(onFormSubmit, handleFormErrors);

  const goToSection = (index) => {
    const clampedIndex = Math.max(0, Math.min(index, sectionOrder.length - 1));
    setActiveSection(sectionOrder[clampedIndex]);
  };

  const persistDraft = () => {
    if (typeof onSaveDraft === 'function') {
      onSaveDraft(getValues());
    }
  };

  const handlePrevSection = () => {
    persistDraft();
    const currentIndex = sectionOrder.indexOf(activeSection);
    if (currentIndex > 0) {
      goToSection(currentIndex - 1);
    }
  };

  const handleNextSection = async () => {
    const currentIndex = sectionOrder.indexOf(activeSection);
    const isLastSection = currentIndex === sectionOrder.length - 1;
    const fieldsToValidate = sectionValidationMap[activeSection] || [];

    if (fieldsToValidate.length > 0) {
      const isValid = await trigger(fieldsToValidate);
      if (!isValid) {
        return;
      }
    }

    persistDraft();

    if (isLastSection) {
      await submitForm();
      return;
    }

    goToSection(currentIndex + 1);
  };

  const currentSectionIndex = sectionOrder.indexOf(activeSection);

  return (
    <div className="h-full flex flex-col">
      {/* Sticky Section Navigation */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide">
            {sections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className={`h-4 w-4 mr-2 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span className="hidden sm:inline">{section.name}</span>
                  <span className="sm:hidden text-xs">{section.name.split(' ')[0]}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Scrollable Form Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <form onSubmit={submitForm}>
              <div className="px-4 sm:px-6 py-6 sm:py-8">
                {/* Sections are hidden rather than unmounted: react-hook-form skips validation of unmounted fields */}
                <div className={activeSection === 'basic' ? '' : 'hidden'}>{renderBasicInfo()}</div>
                <div className={activeSection === 'specifications' ? '' : 'hidden'}>{renderSpecifications()}</div>
              </div>

              {/* Sticky Form Actions */}
              <div className="sticky bottom-0 px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 shadow-lg">
                {vehicleCreated && activeSection === 'documents' ? (
                  // Show document upload action buttons after vehicle creation
                  <>
                    <button
                      type="button"
                      onClick={onCancel}
                      className="w-full sm:w-auto px-6 py-2.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      Add Later
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (onStartUploadingDocuments) {
                          onStartUploadingDocuments();
                        } else {
                          onCancel();
                        }
                      }}
                      className="w-full sm:w-auto px-6 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center justify-center"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Start Uploading
                    </button>
                  </>
                ) : (
                  // Normal navigation buttons
                  <>
                    <button
                      type="button"
                      onClick={onCancel}
                      className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      Cancel
                    </button>
                    <div className="flex w-full sm:w-auto sm:justify-end space-x-3">
                      <button
                        type="button"
                        onClick={handlePrevSection}
                        disabled={currentSectionIndex === 0 || isSubmitting}
                        className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        onClick={handleNextSection}
                        disabled={isSubmitting}
                        className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {(() => {
                          // When editing a vehicle
                          if (vehicle) {
                            if (currentSectionIndex === sectionOrder.length - 1) {
                              return isSubmitting ? 'Saving...' : 'Update Vehicle';
                            }
                            return 'Next';
                          }

                          // When creating a new vehicle - submit on specifications tab (last tab)
                          if (activeSection === 'specifications') {
                            return isSubmitting ? 'Creating Vehicle...' : 'Add Vehicle';
                          }

                          // All other tabs show Next
                          return 'Next';
                        })()}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal />
    </div>
  );
};

export default VehicleForm;
